import React, { useCallback, useEffect, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import {
  AtendimentoResumo,
  buscarVisaoGeralOficina,
  FiltrosOficina,
  RespostaVisaoGeral,
} from '../../../api/dashboardOficinaApi';
import DetalhePlacasModal from '../../../shared/ui/DetalhePlacasModal';
import { ColunaTabela } from '../../../shared/ui/TabelaPaginada';
import ComoFunciona, { ComoFuncionaSecao } from '../../../components/como-funciona/ComoFunciona';
import { notify } from '../../../shared/feedback/notify';
import { corDaSerie, formatarNumero, TINTA_GRAFICO } from '../../../shared/ui/chart-palette';
import {
  AreaEstado,
  CardGrafico,
  EIXO_LINHA,
  EIXO_TICK,
  ESTILO_TOOLTIP,
  Kpi,
  Legenda,
} from '../../../shared/ui/GraficoBase';
import '../../../components/como-funciona/help-circle-btn.css';
import '../../../shared/feedback/erro-de-tela.css';
import '../../../shared/ui/dashboard-base.css';
import RetrabalhoCard from './RetrabalhoCard';

/** Rótulo do tipo de dano na coluna do detalhamento (at-88). */
const ROTULO_DANO: Record<string, string> = {
  PEQUENO: 'Pequeno',
  MEDIO: 'Médio',
  GRANDE: 'Grande',
};

const COR_ESTEIRA = corDaSerie(0);
const COR_ENTRADAS = corDaSerie(0);
const COR_ENTREGAS = corDaSerie(1);

export const helpSectionsVisaoGeralOficina: ComoFuncionaSecao[] = [
  {
    titulo: 'O que esta aba responde',
    conteudo: (
      <>
        <p>
          Como está a oficina <strong>agora</strong> e como ela se comportou no período: quantos
          veículos estão em atendimento, quantos saíram, quanto tempo em média um veículo passa aqui
          e o que está travado.
        </p>
        <p>
          Todos os números respeitam o filtro <strong>Situação</strong> no alto da tela — na
          oficina, entregues ou os dois.
        </p>
      </>
    ),
  },
  {
    titulo: 'Permanência média: o que ela mede',
    conteudo: (
      <>
        <p>
          O tempo entre a <strong>entrada do veículo</strong> e a <strong>entrega ao cliente</strong>
          , contado apenas sobre os atendimentos já <strong>encerrados</strong> no período. Veículo
          ainda na oficina não entra na conta — ele ainda não tem um fim.
        </p>
        <p>
          É tempo de calendário, não de trabalho: inclui espera por peça, por autorização e fins de
          semana. Para saber quanto tempo de equipe cada etapa consome, use a aba{' '}
          <strong>SLA &amp; Tempos</strong>.
        </p>
      </>
    ),
  },
  {
    titulo: 'Aguardando liberação e prazo em risco',
    conteudo: (
      <>
        <p>
          <strong>Aguardando liberação</strong> conta os veículos em atendimento que ainda não
          receberam liberação da administração. Enquanto não recebem, não entram em esteira de
          trabalho, não recebem colaborador e não iniciam atividade — é fila parada, e costuma ser o
          gargalo menos visível da oficina.
        </p>
        <p>
          <strong>Prazo em risco</strong> são os veículos com sete dias ou menos no prazo de 90 dias
          de permanência.
        </p>
      </>
    ),
  },
  {
    titulo: 'Clique nos gráficos e nos indicadores',
    conteudo: (
      <p>
        Toda barra dos gráficos de esteira e todo indicador do topo abrem a lista dos veículos
        que formam aquele número, com placa, modelo, esteira e há quantos dias estão aqui.
      </p>
    ),
  },
  {
    titulo: 'O pico de julho de 2026 não foi um dia movimentado',
    conteudo: (
      <p>
        Sem filtro escolhido, a tela mostra <strong>todo o histórico</strong>. Isso inclui o dia em
        que o sistema entrou no ar: os veículos que já estavam na oficina foram cadastrados{' '}
        <strong>todos de uma vez</strong>, e por isso o gráfico de entradas tem um pico em julho de
        2026 que não corresponde a movimento real. Para analisar a operação do dia a dia, escolha um
        período que comece depois disso.
      </p>
    ),
  },
  {
    titulo: 'Retrabalho: veículos que voltaram em garantia',
    conteudo: (
      <>
        <p>
          O card <strong>Retrabalho</strong> responde se o serviço da oficina está sendo refeito:
          dos veículos <strong>entregues</strong> no período, quantos voltaram como{' '}
          <strong>pós-evento</strong> (retorno de garantia do serviço entregue). Por isso ele olha só
          os entregues e ignora o filtro de situação — e avisa quando você escolhe outra.
        </p>
        <p>
          Na tabela por esteira, um retorno conta em <strong>todas</strong> as etapas pelas quais o
          serviço original passou: o sistema registra que o veículo voltou, mas não qual etapa
          falhou. Leia o número da esteira como "dos veículos que passaram por aqui, quantos
          voltaram", e não como culpa da etapa.
        </p>
        <p>
          O retorno de um serviço feito <strong>fora da oficina</strong> não entra: é o primeiro
          serviço que fazemos naquele veículo, não retrabalho. E a garantia de 90 dias é só
          informativa — o card não separa retornos dentro e fora dela.
        </p>
      </>
    ),
  },
];

interface Props {
  filtros: FiltrosOficina;
  sidebarRef?: React.RefObject<HTMLElement>;
  onJanela?: (janela: { dataInicio: string; dataFim: string }) => void;
  onEsteiras?: (esteiras: Array<{ id: string; nome: string }>) => void;
}

const VisaoGeralOficinaTab: React.FC<Props> = ({ filtros, onJanela, onEsteiras, sidebarRef }) => {
  const [dados, setDados] = useState<RespostaVisaoGeral | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [detalhe, setDetalhe] = useState<{
    titulo: string;
    placas: string[];
    colunas: Array<ColunaTabela<AtendimentoResumo>>;
  } | null>(null);

  const carregar = useCallback(
    async (f: FiltrosOficina) => {
      setCarregando(true);
      try {
        const resposta = await buscarVisaoGeralOficina(f);
        /*
         * ⚠️ Resposta sem o formato esperado é tratada como FALHA, não como dado.
         *
         * Acontece de verdade quando o servidor em execução é anterior à versão da tela (campo
         * renomeado, por exemplo). Sem esta guarda a leitura estoura em `undefined.algo` e
         * derruba a página com erro de runtime — tela branca, que é pior que número faltando.
         */
        if (!resposta || typeof resposta.kpis === 'undefined') {
          throw new Error('Resposta do servidor em formato inesperado');
        }

        /*
         * ⚠️ Resposta sem o formato esperado é tratada como FALHA, não como dado.
         *
         * Acontece de verdade quando o servidor em execução é anterior à versão da tela (campo
         * renomeado, por exemplo). Sem esta guarda a leitura estoura em `undefined.algo` e
         * derruba a página com erro de runtime — tela branca, que é pior que número faltando.
         */
        if (!resposta || typeof resposta.kpis === 'undefined') {
          throw new Error('Resposta do servidor em formato inesperado');
        }

        setDados(resposta);
        setErro(null);
        onJanela?.({ dataInicio: resposta.janela.dataInicio, dataFim: resposta.janela.dataFim });
        onEsteiras?.(resposta.porEsteira.map((e) => ({ id: e.esteiraId, nome: e.esteira })));
      } catch (e) {
        // Estado seguro + aviso: gráfico vazio por erro não pode passar por "oficina sem veículo".
        setDados(null);
        setErro(
          'Não foi possível carregar os indicadores da oficina. Enquanto este aviso estiver na tela, não há número para ler aqui — recarregue a página; se persistir, acione a TI.',
        );
        notify.errorFrom(
          e,
          'Não foi possível carregar os indicadores da oficina. Recarregue a página; se persistir, acione a TI.',
          { toastId: 'dashboard-oficina-visao-geral-falhou' },
        );
      } finally {
        setCarregando(false);
      }
    },
    [onJanela, onEsteiras],
  );

  useEffect(() => {
    carregar(filtros);
  }, [filtros, carregar]);

  const respondeu = dados !== null;
  const primeiraCarga = carregando && !respondeu;

  /** Afirma ausência SÓ quando a consulta respondeu (at-60). */
  const textoVazio = respondeu
    ? 'Nenhum atendimento neste recorte.'
    : 'Não foi possível carregar — veja o aviso acima.';

  const abrir = (
    titulo: string,
    placas: string[],
    colunas: Array<ColunaTabela<AtendimentoResumo>>,
  ) => setDetalhe({ titulo, placas, colunas });

  /**
   * Colunas de cada modal — a informação do INDICADOR clicado, não um conjunto fixo.
   *
   * ⚠️ Quem abre "Com retorno" quer ver quantas vezes cada carro voltou; quem abre "Prazo em
   * risco" quer os dias que faltam; quem abre "Permanência média" precisa da permanência de cada
   * um, senão a média fica sem conferência possível. Um conjunto único serviria mal a todos.
   *
   * ⚠️ Todas são operacionais, do banco da própria oficina: esta aba não consulta o cadastro do
   * SGA, então categoria e valor protegido chegariam vazios aqui.
   */
  const COL = {
    esteira: {
      titulo: 'Esteira',
      largura: 190,
      texto: (i: AtendimentoResumo) => i.esteira,
    },
    diasNaEsteira: {
      titulo: 'Dias nesta esteira',
      largura: 160,
      texto: (i: AtendimentoResumo) => `${i.diasNaEsteiraAtual} dias`,
    },
    diasAqui: {
      titulo: 'Dias na oficina',
      largura: 150,
      texto: (i: AtendimentoResumo) => `${i.diasNaOficina} dias`,
    },
    permanencia: {
      titulo: 'Dias até a entrega',
      largura: 150,
      texto: (i: AtendimentoResumo) => `${i.diasNaOficina} dias`,
    },
    retornos: {
      titulo: 'Retornos',
      largura: 120,
      texto: (i: AtendimentoResumo) => String(i.retornos),
    },
    prazo: {
      titulo: 'Prazo restante',
      largura: 150,
      texto: (i: AtendimentoResumo) => (i.prazoRestante != null ? `${i.prazoRestante} dias` : '—'),
    },
    classe: {
      titulo: 'Classe',
      largura: 140,
      texto: (i: AtendimentoResumo) => i.carClass,
    },
    /*
     * Coluna do próprio recorte (at-88): quem filtrou por tipo de dano precisa ver o dano na
     * linha, senão o detalhamento não explica o número que o abriu. Aparece sempre, porque
     * "Não informado" é informação — é metade da base.
     */
    dano: {
      titulo: 'Tipo de dano',
      largura: 150,
      texto: (i: AtendimentoResumo) => ROTULO_DANO[i.damageType ?? ''] ?? 'Não informado',
    },
  };

  /**
   * Placas de uma esteira, derivadas da lista de apoio.
   *
   * ⚠️ Filtra também por `naOficina`: os dois gráficos de esteira descrevem quem está na
   * oficina AGORA (o backend exclui a esteira de entrega), então incluir entregues abriria um
   * modal com veículos que não estão na barra clicada.
   */
  const placasDaEsteira = (nome: string) =>
    (dados?.atendimentos ?? []).filter((a) => a.naOficina && a.esteira === nome).map((a) => a.plate);

  /**
   * Clique num ponto do fluxo: mostra quem ENTROU e quem SAIU naquele mês, na mesma lista.
   *
   * ⚠️ A coluna "Movimento" distingue os dois — sem ela, as duas séries do gráfico virariam uma
   * lista única e indistinguível, e o número de cada linha do gráfico deixaria de ser conferível.
   */
  const abrirMes = (ponto: {
    mes: string;
    placasEntradas: string[];
    placasEntregas: string[];
  }) => {
    const entradas = new Set(ponto.placasEntradas);
    const placas = Array.from(new Set([...ponto.placasEntradas, ...ponto.placasEntregas]));
    if (placas.length === 0) return;

    abrir(`Movimento de ${ponto.mes}`, placas, [
      {
        titulo: 'Movimento',
        largura: 200,
        texto: (i: AtendimentoResumo) => {
          const entrou = entradas.has(i.plate);
          const saiu = new Set(ponto.placasEntregas).has(i.plate);
          if (entrou && saiu) return 'Entrou e saiu no mês';
          return entrou ? 'Entrou no mês' : 'Saiu no mês';
        },
      },
      COL.esteira,
      COL.diasAqui,
    ]);
  };

  const abrirEsteira = (nome: string) => {
    const placas = placasDaEsteira(nome);
    if (placas.length > 0)
      abrir(`Esteira: ${nome}`, placas, [
        COL.diasNaEsteira,
        COL.diasAqui,
        COL.classe,
        COL.dano,
      ]);
  };

  /** Só vira botão quando há placa por trás — indicador zerado não abre modal vazio. */
  const detalharSe = (
    titulo: string,
    placas: string[] | undefined,
    colunas: Array<ColunaTabela<AtendimentoResumo>>,
  ) => (placas && placas.length > 0 ? () => abrir(titulo, placas, colunas) : undefined);

  const esteiras = dados?.porEsteira ?? [];
  const fluxo = dados?.fluxoMensal ?? [];

  return (
    <div className={carregando && respondeu ? 'do-atualizando' : undefined}>
      {erro && (
        <div className="erro-de-tela" role="alert">
          {erro}
        </div>
      )}

      <div className="do-aba-cabecalho">
        <div className="do-aba-titulo">
          Visão Geral da Oficina
          <button
            type="button"
            className="help-circle-btn"
            title="Como Funciona"
            onClick={() => setIsHelpOpen(true)}
          >
            ?
          </button>
        </div>
      </div>

      <div className="do-kpis">
        <Kpi
          rotulo="Na oficina agora"
          valor={respondeu ? formatarNumero(dados.kpis.naOficinaAgora) : '-'}
          apoio="Atendimentos em andamento"
          carregando={primeiraCarga}
          onDetalhar={detalharSe('Na oficina agora', dados?.placasPorIndicador?.naOficinaAgora, [COL.esteira, COL.diasAqui])}
          detalheRotulo="Ver os veículos que estão na oficina"
        />
        <Kpi
          rotulo="Entregues no período"
          valor={respondeu ? formatarNumero(dados.kpis.entreguesNoPeriodo) : '-'}
          apoio="Atendimentos encerrados"
          carregando={primeiraCarga}
          onDetalhar={detalharSe('Entregues no período', dados?.placasPorIndicador?.entreguesNoPeriodo, [COL.permanencia, COL.classe])}
          detalheRotulo="Ver os atendimentos encerrados"
        />
        <Kpi
          rotulo="Permanência média"
          valor={respondeu ? `${dados.kpis.permanenciaMediaDias} dias` : '-'}
          apoio="Da entrada até a entrega, só dos encerrados"
          carregando={primeiraCarga}
          onDetalhar={detalharSe('Permanência média', dados?.placasPorIndicador?.permanenciaMediaDias, [COL.permanencia, COL.esteira])}
          detalheRotulo="Ver os atendimentos que entram nesta média"
        />
        <Kpi
          rotulo="Aguardando liberação"
          valor={respondeu ? formatarNumero(dados.kpis.aguardandoLiberacao) : '-'}
          apoio="Sem liberação da administração"
          carregando={primeiraCarga}
          onDetalhar={detalharSe('Aguardando liberação', dados?.placasPorIndicador?.aguardandoLiberacao, [COL.esteira, COL.diasAqui])}
          detalheRotulo="Ver os veículos sem liberação"
        />
        <Kpi
          rotulo="Com retorno"
          valor={respondeu ? formatarNumero(dados.kpis.atendimentosComRetorno) : '-'}
          apoio="Veículos que já voltaram à oficina"
          carregando={primeiraCarga}
          onDetalhar={detalharSe('Com retorno', dados?.placasPorIndicador?.atendimentosComRetorno, [COL.retornos, COL.esteira])}
          detalheRotulo="Ver os veículos que já voltaram"
        />
        <Kpi
          rotulo="Prazo em risco"
          valor={respondeu ? formatarNumero(dados.kpis.prazoEmRisco) : '-'}
          apoio="Sete dias ou menos no prazo de 90"
          carregando={primeiraCarga}
          onDetalhar={detalharSe('Prazo em risco', dados?.placasPorIndicador?.prazoEmRisco, [COL.prazo, COL.esteira])}
          detalheRotulo="Ver os veículos com prazo apertado"
        />
      </div>

      <div className="do-grid">
        <CardGrafico
          titulo="Veículos por esteira"
          descricao="Onde estão os veículos em atendimento agora. Esteiras sem veículo não aparecem. Clique numa barra para ver as placas."
        >
          <div className="do-plot">
            {!respondeu || esteiras.length === 0 ? (
              <AreaEstado carregando={primeiraCarga} mensagem={textoVazio} />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={esteiras} layout="vertical" margin={{ left: 8, right: 16 }}>
                  <CartesianGrid stroke={TINTA_GRAFICO.grade} horizontal={false} />
                  <XAxis type="number" tick={EIXO_TICK} axisLine={EIXO_LINHA} tickLine={false} allowDecimals={false} />
                  <YAxis
                    type="category"
                    dataKey="esteira"
                    tick={EIXO_TICK}
                    axisLine={EIXO_LINHA}
                    tickLine={false}
                    width={130}
                  />
                  <Tooltip
                    contentStyle={ESTILO_TOOLTIP}
                    formatter={(valor: number) => [formatarNumero(valor), 'Veículos']}
                  />
                  <Bar
                    dataKey="veiculos"
                    radius={[0, 4, 4, 0]}
                    cursor="pointer"
                    onClick={(evento: any) => evento?.payload && abrirEsteira(evento.payload.esteira)}
                  >
                    {esteiras.map((item) => (
                      <Cell key={item.esteiraId} fill={COR_ESTEIRA} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </CardGrafico>

        <CardGrafico
          titulo="Tempo médio na esteira atual"
          descricao="Há quantos dias, em média, os veículos de cada esteira estão nela. Número alto indica fila parada. Clique numa barra para ver as placas."
        >
          <div className="do-plot">
            {!respondeu || esteiras.length === 0 ? (
              <AreaEstado carregando={primeiraCarga} mensagem={textoVazio} />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={esteiras} margin={{ left: 8, right: 16 }}>
                  <CartesianGrid stroke={TINTA_GRAFICO.grade} vertical={false} />
                  <XAxis dataKey="esteira" tick={EIXO_TICK} axisLine={EIXO_LINHA} tickLine={false} />
                  <YAxis tick={EIXO_TICK} axisLine={EIXO_LINHA} tickLine={false} />
                  <Tooltip
                    contentStyle={ESTILO_TOOLTIP}
                    formatter={(valor: number) => [`${valor} dias`, 'Média']}
                  />
                  <Bar
                    dataKey="diasMedios"
                    fill={corDaSerie(2)}
                    radius={[4, 4, 0, 0]}
                    cursor="pointer"
                    onClick={(evento: any) => evento?.payload && abrirEsteira(evento.payload.esteira)}
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </CardGrafico>
      </div>

      <div className="do-grid-largo">
        <CardGrafico
          titulo="Entradas e entregas por mês"
          descricao="Entradas contam pela data de entrada do veículo; entregas, pela data em que o atendimento foi encerrado. Clique num mês para ver os veículos que entraram e saíram nele."
        >
          <Legenda
            itens={[
              { rotulo: 'Entradas', cor: COR_ENTRADAS },
              { rotulo: 'Entregas', cor: COR_ENTREGAS },
            ]}
          />
          <div className="do-plot">
            {!respondeu || fluxo.length === 0 ? (
              <AreaEstado carregando={primeiraCarga} mensagem={textoVazio} />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={fluxo}
                  margin={{ left: 8, right: 16 }}
                  style={{ cursor: fluxo.length > 0 ? 'pointer' : undefined }}
                  onClick={(evento: any) => {
                    const ponto = evento?.activePayload?.[0]?.payload;
                    if (ponto) abrirMes(ponto);
                  }}
                >
                  <CartesianGrid stroke={TINTA_GRAFICO.grade} vertical={false} />
                  <XAxis dataKey="mes" tick={EIXO_TICK} axisLine={EIXO_LINHA} tickLine={false} />
                  <YAxis tick={EIXO_TICK} axisLine={EIXO_LINHA} tickLine={false} allowDecimals={false} />
                  <Tooltip contentStyle={ESTILO_TOOLTIP} />
                  <Line type="monotone" dataKey="entradas" name="Entradas" stroke={COR_ENTRADAS} strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="entregas" name="Entregas" stroke={COR_ENTREGAS} strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </CardGrafico>
      </div>

      {/* at-133 — carga própria: a falha do card não apaga a aba, nem a da aba apaga o card. */}
      <RetrabalhoCard filtros={filtros} sidebarRef={sidebarRef} />

      {detalhe && dados && (
        <DetalhePlacasModal
          titulo={detalhe.titulo}
          placas={detalhe.placas}
          colunas={detalhe.colunas}
          atendimentos={dados.atendimentos}
          onFechar={() => setDetalhe(null)}
          sidebarRef={sidebarRef}
        />
      )}

      <ComoFunciona
        isOpen={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
        titulo="Como Funciona - Visão Geral da Oficina"
        secoes={helpSectionsVisaoGeralOficina}
      />
    </div>
  );
};

export default VisaoGeralOficinaTab;
